import { useEffect, useRef, useState, useCallback } from "react";
import { renderMarkdown } from '../utils/renderMarkdown';
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useNavigate } from "react-router-dom";
import PostLoader from '../assets/img/loader.gif';
import API from "../Network/API";
import {
  SUGGESTED_VIDOE_DELETE_COMMENT,
  SUGGESTED_VIDOE_POST_FETCH_COMMENT_ADDON,
  SUGGESTED_VIDOE_POST_FETCH_SUCCESS_LOAD,
  SUGGESTED_VIDOE_POST_FETCH_SUCCESS_LOAD2,
} from "../Reducer/suggestedVideoReducer";
import { SINGLE_DELETE_COMMENT, SINGLE_POST_FETCH_COMMENT_ADDON } from "../Reducer/singlePostReducer";
import { FPOST_FETCH_COMMENT_ADDON } from "../Reducer/friendProfileReducer";
import { toast } from "react-toastify";
import PostShareModal from "../Props/PostShareModal";

/* ─── Suppress AbortError from video.play() ───
   Mobile browsers throw AbortError when play() is interrupted by pause().
   This is expected behavior, not a bug. React dev mode catches it via
   window.onunhandledrejection before .catch() handlers run. */
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (e) => {
    if (e.reason && (e.reason.name === 'AbortError' || e.reason.message === 'The operation was aborted.')) {
      e.preventDefault();
    }
  });
}

/* ────────────────────────────────────────────────────────
   Retry helper — exponential backoff (3 attempts, 1s/2s/4s)
   ──────────────────────────────────────────────────────── */
function fetchWithRetry(fetchFn, maxRetries = 3) {
  return new Promise((resolve, reject) => {
    let attempt = 0;
    const tryFetch = () => {
      fetchFn()
        .then(resolve)
        .catch((err) => {
          attempt++;
          if (attempt >= maxRetries) return reject(err);
          const delay = Math.pow(2, attempt - 1) * 1000; // 1s, 2s, 4s
          setTimeout(tryFetch, delay);
        });
    };
    tryFetch();
  });
}

/* ────────────────────────────────────────────────────────
   Connection helper — detect slow network
   ──────────────────────────────────────────────────────── */
function isSlowConnection() {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return false;
  const slowTypes = ['slow-2g', '2g', '3g'];
  return slowTypes.includes(conn.effectiveType) || conn.saveData;
}

/* ────────────────────────────────────────────────────────
   Safe play — prevents unhandled rejection from vid.play()
   Key: attach a no-op .catch() on the RAW promise immediately
   so the browser marks it as handled before React's error
   overlay can intercept it via onunhandledrejection.
   ──────────────────────────────────────────────────────── */
function doSafePlay(vid, promiseRef) {
  try {
    const p = vid.play();
    if (p && typeof p.catch === 'function') {
      // Mark the raw promise as handled IMMEDIATELY
      p.catch(() => { });
      promiseRef.current = p;
      // Return a separate chain for our logic
      return p.then(() => { promiseRef.current = null; return true; },
        () => { promiseRef.current = null; return false; });
    }
    // Older browsers: play() returns undefined
    return Promise.resolve(true);
  } catch (e) {
    return Promise.resolve(false);
  }
}

/* ────────────────────────────────────────────────────────
   ReelItem — a single full-screen reel with IntersectionObserver
   Handles: autoplay, pause, mute, loop, tap-to-pause
   ──────────────────────────────────────────────────────── */
function ReelItem({ post, isActive, isNearby, onBecameVisible, globalMuted, onToggleMute }) {
  const videoRef = useRef(null);
  const observerRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayIcon, setShowPlayIcon] = useState(false);
  const [showPauseIcon, setShowPauseIcon] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const playIconTimeout = useRef(null);
  const pauseIconTimeout = useRef(null);
  const lastTapTime = useRef(0);
  const heartTimeout = useRef(null);
  const playPromiseRef = useRef(null); // track pending play() to avoid AbortError

  // Helper: safely play video — NEVER throws, NEVER creates unhandled rejections
  const safePlay = useCallback((vid) => {
    if (!vid) return Promise.resolve(false);

    // If a previous play() is still pending, wait for it
    const pending = playPromiseRef.current;
    if (pending) {
      return pending.finally(() => doSafePlay(vid, playPromiseRef));
    }
    return doSafePlay(vid, playPromiseRef);
  }, []);

  // ─── IntersectionObserver: auto-play when >50% visible ───
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          onBecameVisible?.(post.id);
        }
      },
      { threshold: [0.5] }
    );
    observerRef.current.observe(el);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [post.id, onBecameVisible]);

  // ─── Play/pause based on active state ───
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;

    if (isActive) {
      vid.muted = globalMuted;

      // If video has enough data, play immediately. Otherwise wait for canplay.
      const attemptPlay = async () => {
        setIsBuffering(false);
        let ok = await safePlay(vid);
        if (!ok && vid.paused) {
          // Autoplay blocked — try muted
          vid.muted = true;
          onToggleMute?.(true);
          ok = await safePlay(vid);
        }
        setIsPlaying(!vid.paused);
      };

      if (vid.readyState >= 3) { // HAVE_FUTURE_DATA or better
        attemptPlay();
      } else {
        // Show spinner while waiting for data
        setIsBuffering(true);
        vid.addEventListener('canplay', attemptPlay, { once: true });
        // Kick off loading if needed
        if (vid.preload === 'none' || vid.preload === 'metadata') {
          vid.preload = 'auto';
          vid.load();
        }
        return () => vid.removeEventListener('canplay', attemptPlay);
      }
    } else {
      vid.pause();
      setIsPlaying(false);
      setIsBuffering(false);
    }
  }, [isActive, globalMuted, safePlay]);

  // ─── Show spinner during buffering stalls ───
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onWaiting = () => { if (isActive) setIsBuffering(true); };
    const onPlaying = () => setIsBuffering(false);
    vid.addEventListener('waiting', onWaiting);
    vid.addEventListener('playing', onPlaying);
    return () => {
      vid.removeEventListener('waiting', onWaiting);
      vid.removeEventListener('playing', onPlaying);
    };
  }, [isActive]);

  // ─── Keep mute state in sync ───
  useEffect(() => {
    const vid = videoRef.current;
    if (vid) vid.muted = globalMuted;
  }, [globalMuted]);

  // ─── Tap to pause/play + double-tap to like ───
  const handleTapVideo = useCallback((e) => {
    // Don't toggle if clicking on an action button
    if (e.target.closest('.reel-action-bar') || e.target.closest('.reel-info-overlay') || e.target.closest('.reel-top-bar')) return;

    const now = Date.now();
    const DOUBLE_TAP_THRESHOLD = 300;

    if (now - lastTapTime.current < DOUBLE_TAP_THRESHOLD) {
      // Double tap → like
      lastTapTime.current = 0;
      setShowHeartAnimation(true);
      clearTimeout(heartTimeout.current);
      heartTimeout.current = setTimeout(() => setShowHeartAnimation(false), 800);
      API.post("/post_likes", { post_id: post.id, react_icon: 1 }).catch(() => { });
      return;
    }

    lastTapTime.current = now;

    // Delay single-tap action to distinguish from double-tap
    setTimeout(() => {
      if (Date.now() - lastTapTime.current < DOUBLE_TAP_THRESHOLD) return; // cancelled by double-tap

      const vid = videoRef.current;
      if (!vid) return;

      if (vid.paused) {
        safePlay(vid).then(() => setIsPlaying(true)).catch(() => { });
        setShowPlayIcon(true);
        clearTimeout(playIconTimeout.current);
        playIconTimeout.current = setTimeout(() => setShowPlayIcon(false), 600);
      } else {
        vid.pause();
        setIsPlaying(false);
        setShowPauseIcon(true);
        clearTimeout(pauseIconTimeout.current);
        pauseIconTimeout.current = setTimeout(() => setShowPauseIcon(false), 600);
      }
    }, DOUBLE_TAP_THRESHOLD);
  }, [post.id]);

  // ─── Preload strategy: active=auto, nearby=auto, far=metadata, slow+far=none ───
  const preloadValue = isActive ? "auto" : (isNearby ? "auto" : (isSlowConnection() ? "none" : "metadata"));

  // ─── Handle video error ───
  const handleVideoError = useCallback(() => {
    setVideoError(true);
    setIsPlaying(false);
  }, []);

  const handleRetryVideo = useCallback((e) => {
    e.stopPropagation();
    setVideoError(false);
    const vid = videoRef.current;
    if (vid) {
      vid.load();
      safePlay(vid).then(() => setIsPlaying(true)).catch(() => { });
    }
  }, [safePlay]);

  return (
    <div className="reel-snap-item" ref={containerRef} onClick={handleTapVideo}>
      {/* Blurred background */}
      {post.videos?.[0]?.thumbnail && (
        <div className="reel-blur-bg">
          <img src={post.videos[0].thumbnail} alt="" />
        </div>
      )}

      {/* Video element */}
      <video
        ref={videoRef}
        src={post.videos?.[0]?.video_stream}
        poster={post.videos?.[0]?.thumbnail}
        preload={preloadValue}
        loop
        playsInline
        webkit-playsinline="true"
        className="reel-video-element"
        onError={handleVideoError}
      />

      {/* Buffering spinner */}
      {isBuffering && isActive && (
        <div className="reel-tap-icon">
          <div className="spinner-border text-light" role="status" style={{ width: 48, height: 48 }} />
        </div>
      )}

      {/* Video error state */}
      {videoError && (
        <div className="reel-error-overlay" onClick={(e) => e.stopPropagation()}>
          <i className="fa-solid fa-triangle-exclamation" />
          <p>Video failed to load</p>
          <button onClick={handleRetryVideo}>Tap to retry</button>
        </div>
      )}

      {/* Double-tap heart animation */}
      {showHeartAnimation && (
        <div className="reel-heart-animation">
          <i className="fa-solid fa-heart" />
        </div>
      )}

      {/* Play/Pause tap feedback icons */}
      {showPlayIcon && (
        <div className="reel-tap-icon">
          <i className="fa-solid fa-play" />
        </div>
      )}
      {showPauseIcon && (
        <div className="reel-tap-icon">
          <i className="fa-solid fa-pause" />
        </div>
      )}

      {/* Paused state: large play button */}
      {!isPlaying && !showPlayIcon && !showPauseIcon && isActive && (
        <div className="reel-paused-overlay">
          <i className="fa-solid fa-play" />
        </div>
      )}

      {/* Top bar: close button */}
      <div className="reel-top-bar">
        <Link to="/" className="reel-close-btn">
          <i className="fa-solid fa-xmark" />
        </Link>
        <span className="reel-top-title">Reels</span>
        <div style={{ width: 40 }} /> {/* spacer */}
      </div>

      {/* Bottom info overlay */}
      <div className="reel-info-overlay">
        <div className="reel-user-info">
          <Link to={`/contact/${post.user_id}`}>
            <div className="reel-user-avatar" style={{ backgroundImage: `url(${post.avatar})` }} />
          </Link>
          <div className="reel-user-meta">
            <Link to={`/contact/${post.user_id}`} className="reel-user-name">
              {post.post_by}
            </Link>
            <span className="reel-post-time">{post.post_time}</span>
          </div>
        </div>
        {post.details && (
          <p className="reel-description" dir="auto" dangerouslySetInnerHTML={{ __html: renderMarkdown(post.details) }} />
        )}
      </div>

      {/* Right action bar */}
      <ReelActionBar post={post} />

      {/* Mute/unmute button */}
      <button className="reel-mute-btn" onClick={(e) => { e.stopPropagation(); onToggleMute?.(!globalMuted); }}>
        {globalMuted ? (
          <i className="fas fa-volume-mute" />
        ) : (
          <i className="fas fa-volume-up" />
        )}
      </button>

      {/* Video progress bar */}
      <VideoProgressBar videoRef={videoRef} isPlaying={isPlaying} />
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   VideoProgressBar — thin bar at the bottom tracking playback
   ──────────────────────────────────────────────────────── */
function VideoProgressBar({ videoRef, isPlaying }) {
  const barRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const update = () => {
      const vid = videoRef.current;
      const bar = barRef.current;
      if (vid && bar && vid.duration) {
        const pct = (vid.currentTime / vid.duration) * 100;
        bar.style.width = `${pct}%`;
      }
      rafRef.current = requestAnimationFrame(update);
    };
    rafRef.current = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafRef.current);
  }, [videoRef, isPlaying]);

  return (
    <div className="reel-progress-track">
      <div className="reel-progress-fill" ref={barRef} />
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   ReelActionBar — like, comment, share buttons (right side)
   ──────────────────────────────────────────────────────── */
function ReelActionBar({ post }) {
  const dispatch = useDispatch();
  const [liked, setLiked] = useState(!!post.is_like);
  const [likeCount, setLikeCount] = useState(post.totalLikes || 0);
  const [showComments, setShowComments] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const handleLike = (e) => {
    e.stopPropagation();
    if (liked) return;
    setLiked(true);
    setLikeCount(prev => prev + 1);
    API.post("/post_likes", { post_id: post.id, react_icon: 1 })
      .then(res => { if (!res.data.success) { setLiked(false); setLikeCount(prev => prev - 1); } })
      .catch(() => { setLiked(false); setLikeCount(prev => prev - 1); });
  };

  return (
    <>
      <div className="reel-action-bar" onClick={(e) => e.stopPropagation()}>
        <button className="reel-action-btn" onClick={handleLike}>
          <i className={`fa-solid fa-heart ${liked ? 'reel-liked' : ''}`} />
          <span>{likeCount}</span>
        </button>
        <button className="reel-action-btn" onClick={(e) => { e.stopPropagation(); setShowComments(true); }}>
          <i className="fa-solid fa-comment" />
          <span>{post.totalComments || 0}</span>
        </button>
        <button className="reel-action-btn" onClick={(e) => { e.stopPropagation(); setShowShare(true); }}>
          <i className="fa-solid fa-share-nodes" />
        </button>
      </div>

      {/* Comment bottom sheet */}
      {showComments && (
        <ReelCommentSheet
          post={post}
          onClose={() => setShowComments(false)}
        />
      )}

      {/* Share modal */}
      <PostShareModal
        isModalVisible={showShare}
        handleShowB={() => setShowShare(false)}
        postId={post.id}
        post_data={post}
      />
    </>
  );
}

/* ────────────────────────────────────────────────────────
   ReelCommentSheet — slide-up comment panel
   ──────────────────────────────────────────────────────── */
function ReelCommentSheet({ post, onClose }) {
  const dispatch = useDispatch();
  const [comments, setComments] = useState(post.last_comments || []);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);

  const submitComment = () => {
    if (!newComment.trim()) return;
    setLoading(true);
    API.post("/post_comments", { post_id: post.id, comment: newComment })
      .then((response) => {
        setLoading(false);
        setNewComment("");
        if (response.data.success) {
          setComments(prev => [...prev, response.data.data]);
          dispatch(SUGGESTED_VIDOE_POST_FETCH_COMMENT_ADDON(response.data.data));
          dispatch(FPOST_FETCH_COMMENT_ADDON(response.data.data));
          dispatch(SINGLE_POST_FETCH_COMMENT_ADDON(response.data.data));
        }
      })
      .catch(() => setLoading(false));
  };

  const deleteComment = (commentId) => {
    API.delete("/comment_delete/" + commentId).then((response) => {
      if (response.data.success) {
        setComments(prev => prev.filter(c => c.id !== commentId));
        const payload = { comment_id: commentId, post_id: post.id };
        dispatch(SUGGESTED_VIDOE_DELETE_COMMENT(payload));
        dispatch(SINGLE_DELETE_COMMENT(payload));
      }
    });
  };

  return (
    <div className="reel-comment-sheet" onClick={(e) => e.stopPropagation()}>
      <div className="reel-comment-header">
        <h6>Comments ({comments.length})</h6>
        <button className="reel-comment-close" onClick={onClose}>
          <i className="fa-solid fa-xmark" />
        </button>
      </div>
      <div className="reel-comment-list">
        {comments.length === 0 ? (
          <p className="reel-comment-empty">No comments yet. Be the first!</p>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="reel-comment-item">
              <Link to={`/contact/${comment.comment_user_id}`}>
                <div className="reel-comment-avatar" style={{ backgroundImage: `url(${comment.avatar})` }} />
              </Link>
              <div className="reel-comment-body">
                <div className="reel-comment-meta">
                  <Link to={`/contact/${comment.comment_user_id}`} className="reel-comment-name">
                    {comment.comment_by}
                  </Link>
                  <span className="reel-comment-time">{comment.comment_time}</span>
                </div>
                <p className="reel-comment-text">{comment.comment}</p>
              </div>
              {comment.is_deletable === 1 && (
                <button className="reel-comment-delete" onClick={() => deleteComment(comment.id)}>
                  <i className="fa-solid fa-trash-can" />
                </button>
              )}
            </div>
          ))
        )}
      </div>
      <div className="reel-comment-input-area">
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Write a comment..."
          onKeyDown={(e) => e.key === 'Enter' && submitComment()}
        />
        <button onClick={submitComment} disabled={loading}>
          {loading ? (
            <span className="spinner-border spinner-border-sm" role="status" />
          ) : (
            <i className="fa-solid fa-paper-plane" />
          )}
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   MAIN COMPONENT — SingleRealVideo
   Full-screen vertical scroll-snap reels experience
   ════════════════════════════════════════════════════════ */
export default function SingleRealVideo() {
  let { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selector_video = useSelector((state) => state.suggestedVideoReducer);
  const post_data = selector_video?.post_data;

  const [isLoading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activePostId, setActivePostId] = useState(parseInt(id, 10));
  const [globalMuted, setGlobalMuted] = useState(true); // Start muted (browser autoplay policy)
  const [page, setPage] = useState(1);
  const scrollContainerRef = useRef(null);
  const initialScrollDone = useRef(false);

  // ─── Load initial data with retry ───
  const loadInitialData = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    fetchWithRetry(() => API.get(`/get_reels`))
      .then(response => {
        dispatch(SUGGESTED_VIDOE_POST_FETCH_SUCCESS_LOAD2(response.data.data.data));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadError(true);
      });
  }, [dispatch]);

  useEffect(() => {
    if (!post_data || post_data.length === 0) {
      loadInitialData();
    } else {
      setLoading(false);
    }
  }, []);

  // ─── Also fetch the specific post's comments ───
  useEffect(() => {
    fetchWithRetry(() => API.get(`/get_post_comments/${id}`))
      .then(response => {
        dispatch(SUGGESTED_VIDOE_POST_FETCH_SUCCESS_LOAD2([response.data.data]));
      }).catch(() => { });
  }, [id]);

  // ─── Scroll to the initial reel on first render ───
  useEffect(() => {
    if (!initialScrollDone.current && post_data?.length > 0 && scrollContainerRef.current) {
      const idx = post_data.findIndex(p => p.id === parseInt(id, 10));
      if (idx >= 0) {
        const children = scrollContainerRef.current.children;
        if (children[idx]) {
          // Use instant scroll to avoid animation on load
          children[idx].scrollIntoView({ behavior: 'instant', block: 'start' });
          initialScrollDone.current = true;
        }
      }
    }
  }, [post_data, id]);

  // ─── Callback when a reel becomes visible (>50%) ───
  const handleReelVisible = useCallback((postId) => {
    setActivePostId(postId);

    // Update URL without full navigation to keep scroll position
    const newPath = `/shorts/${postId}`;
    if (window.location.pathname !== newPath) {
      window.history.replaceState(null, '', newPath);
    }

    // Infinite scroll: load more with retry when nearing the end
    if (post_data) {
      const idx = post_data.findIndex(p => p.id === postId);
      if (idx >= post_data.length - 3 && selector_video?.data?.next_page_url) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchWithRetry(() => API.get(`/get_reels?page=${nextPage}`))
          .then(response => {
            dispatch(SUGGESTED_VIDOE_POST_FETCH_SUCCESS_LOAD(response.data.data));
          }).catch(() => { });
      }
    }

    // Record view (fire-and-forget, no retry needed)
    API.post('/reel_view', { post_id: postId, watch_duration_ms: 0, completed: false }).catch(() => { });
  }, [post_data, page, selector_video?.data?.next_page_url, dispatch]);

  // ─── Toggle global mute ───
  const handleToggleMute = useCallback((muted) => {
    setGlobalMuted(muted);
  }, []);

  // ─── Handle keyboard navigation ───
  useEffect(() => {
    const handleKeyDown = (e) => {
      const container = scrollContainerRef.current;
      if (!container) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const scrollAmount = e.key === 'ArrowDown' ? container.clientHeight : -container.clientHeight;
        container.scrollBy({ top: scrollAmount, behavior: 'smooth' });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ─── Loading state ───
  if (isLoading) {
    return (
      <div className="reel-loading-screen">
        <img src={PostLoader} alt="Loading" height={80} width={80} />
      </div>
    );
  }

  // ─── Error state with retry ───
  if (loadError || (!post_data || post_data.length === 0)) {
    return (
      <div className="reel-loading-screen">
        <div className="reel-error-state">
          <i className="fa-solid fa-wifi" style={{ fontSize: 48, color: '#666', marginBottom: 16 }} />
          <p style={{ color: '#999', fontSize: 16, marginBottom: 16 }}>Failed to load reels</p>
          <button className="reel-retry-btn" onClick={loadInitialData}>
            <i className="fa-solid fa-rotate-right" />&nbsp; Tap to retry
          </button>
          <Link to="/" style={{ color: '#0096ff', marginTop: 12, textDecoration: 'none' }}>Go back home</Link>
        </div>
      </div>
    );
  }

  // Deduplicate posts (backend may return overlapping pages)
  const uniquePosts = post_data.filter((post, index, self) =>
    index === self.findIndex(p => p.id === post.id)
  );

  return (
    <div className="reels-scroll-container" ref={scrollContainerRef}>
      {uniquePosts.map((post, index) => {
        if (!post.videos || post.videos.length === 0) return null;
        const activeIndex = uniquePosts.findIndex(p => p.id === activePostId);
        return (
          <ReelItem
            key={post.id}
            post={post}
            isActive={activePostId === post.id}
            isNearby={Math.abs(index - activeIndex) <= 1}
            onBecameVisible={handleReelVisible}
            globalMuted={globalMuted}
            onToggleMute={handleToggleMute}
          />
        );
      })}
    </div>
  );
}